import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, User as UserIcon } from "lucide-react";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { Label } from "@/modern/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { UserAvatar } from "@/modern/components/user-avatar";
import { cn } from "@/modern/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import type { Checklist } from "@/react/components/entityViews/checklist/types";

const AVATAR_STACK_SIZE_PX = 28;
const AVATAR_STACK_OVERLAP_PX = 6;
const AVATAR_STACK_STEP_PX = AVATAR_STACK_SIZE_PX - AVATAR_STACK_OVERLAP_PX;

type BoardUserLike = {
  id: number;
  name: string;
  profileImageURL?: string;
  archived?: boolean;
  email?: string;
};

function stackWidthPx(itemCount: number) {
  if (itemCount <= 0) return 0;
  return AVATAR_STACK_SIZE_PX + (itemCount - 1) * AVATAR_STACK_STEP_PX;
}

/** How many face avatars fit before the +N chip, given available width. */
function visibleAvatarCount(total: number, availablePx: number) {
  if (total <= 0 || availablePx <= 0) return total;
  for (let visible = total; visible >= 0; visible -= 1) {
    const overflowChip = total - visible > 0 ? 1 : 0;
    if (stackWidthPx(visible + overflowChip) <= availablePx) return visible;
  }
  return 0;
}

function useFitAvatarCount(total: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(total);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      setVisible(visibleAvatarCount(total, el.clientWidth));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [total]);

  return { ref, visible };
}

function AssignedUsersList({
  searchQuery,
  setSearchQuery,
  filteredUsers,
  selectedUserIds,
  toggleUser,
}: {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filteredUsers: BoardUserLike[];
  selectedUserIds: number[];
  toggleUser: (userId: number) => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2 border-b border-border/60 px-3">
        <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search users…"
          aria-label="Search users"
          className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground focus:outline-none"
        />
      </div>
      <div
        className="h-56 overflow-y-auto overscroll-contain p-1"
        onWheel={(e) => e.stopPropagation()}
        role="listbox"
        aria-multiselectable="true"
        aria-label="Board users"
      >
        {filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-3 py-10 text-center">
            <UserIcon className="size-7 text-muted-foreground/40" aria-hidden />
            <p className="text-sm text-muted-foreground">No users found</p>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            {filteredUsers.map((user) => {
              const isSelected = selectedUserIds.includes(user.id);
              return (
                <button
                  key={user.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                    "outline-none transition-colors duration-150 ease-out",
                    "hover:bg-accent focus-visible:bg-accent",
                    "focus-visible:ring-0 focus-visible:ring-offset-0",
                    isSelected && "bg-accent text-accent-foreground",
                  )}
                  onClick={() => toggleUser(user.id)}
                >
                  <UserAvatar user={user} size="small" showName={true} className="min-w-0" />
                  <Checkbox
                    checked={isSelected}
                    tabIndex={-1}
                    aria-hidden
                    onCheckedChange={() => toggleUser(user.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="pointer-events-none shrink-0"
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

interface BoardUserSelectFieldProps {
  checklist?: Checklist;
  userIds?: number[];
  label?: string;
  disabled?: boolean;
  onChange?: (userIds: number[]) => void;
  /** `select` matches Permissions fields; `avatars` is the CheckGrid tray stack. */
  variant?: "select" | "avatars";
}

export function BoardUserSelectField({
  checklist,
  userIds,
  label = "Assigned to",
  disabled,
  onChange,
  variant = "select",
}: BoardUserSelectFieldProps) {
  const { project } = useSwivuBryntumContext();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(
    () => [...(userIds ?? checklist?.assignedBoardUsers ?? [])],
  );

  useEffect(() => {
    setSelectedUserIds([...(userIds ?? checklist?.assignedBoardUsers ?? [])]);
  }, [checklist, checklist?.assignedBoardUsers, userIds]);

  const boardUsers = useMemo(() => {
    if (!project?.boardUsersStore) return [];
    const users: BoardUserLike[] = [];
    project.boardUsersStore.forEach((user: BoardUserLike & { isDeactivated?: boolean }) => {
      if (!user.archived && !user.isDeactivated) {
        users.push({
          id: user.id,
          name: user.name,
          profileImageURL: user.profileImageURL,
          archived: user.archived,
          email: user.email,
        });
      }
    });
    return users;
  }, [project]);

  const selectedUsers = useMemo(
    () =>
      selectedUserIds
        .map((id) => boardUsers.find((u) => u.id === id) ?? project?.boardUsersStore?.getById(id))
        .filter(Boolean) as BoardUserLike[],
    [selectedUserIds, boardUsers, project],
  );

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return boardUsers;
    const q = searchQuery.toLowerCase();
    return boardUsers.filter(
      (user) => user.name?.toLowerCase().includes(q) || user.email?.toLowerCase().includes(q),
    );
  }, [boardUsers, searchQuery]);

  const toggleUser = (userId: number) => {
    const next = selectedUserIds.includes(userId)
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId];
    setSelectedUserIds(next);
    onChange?.(next);
  };

  const isAvatars = variant === "avatars";
  const { ref: avatarFitRef, visible: visibleAvatarSlots } = useFitAvatarCount(
    isAvatars ? selectedUsers.length : 0,
  );
  const visibleUsers = isAvatars ? selectedUsers.slice(0, visibleAvatarSlots) : selectedUsers.slice(0, 3);
  const overflowCount = isAvatars
    ? Math.max(0, selectedUsers.length - visibleAvatarSlots)
    : Math.max(0, selectedUsers.length - 3);
  const assignedLabel =
    selectedUsers.length === 0
      ? "Assign people"
      : selectedUsers.length === 1
        ? selectedUsers[0].name
        : `${selectedUsers.length} people assigned`;

  const popoverBody = (
    <AssignedUsersList
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filteredUsers={filteredUsers}
      selectedUserIds={selectedUserIds}
      toggleUser={toggleUser}
    />
  );

  if (isAvatars) {
    if (!checklist || !selectedUsers.length) return null;
    return (
      <div className="w-full min-w-0 space-y-1.5">
        <p className="text-[11.5px] font-medium tracking-wide text-muted-foreground uppercase">Assigned to</p>
        <div ref={avatarFitRef} className="w-full min-w-0">
          <Popover
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              if (!next) setSearchQuery("");
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                role="combobox"
                aria-expanded={open}
                aria-label={assignedLabel}
                className={cn(
                  "flex w-full items-center justify-start rounded-full outline-none",
                  "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  open && "opacity-90",
                )}
              >
                <span className="flex min-w-0 items-center -space-x-1.5">
                  {visibleUsers.map((user) => (
                    <UserAvatar
                      key={user.id}
                      user={user}
                      size="small"
                      showTooltip={true}
                      showRemove={false}
                      avatarClassName="!size-7 text-[11px] ring-2 ring-background"
                    />
                  ))}
                  {overflowCount > 0 ? (
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-2 ring-background">
                      +{overflowCount}
                    </span>
                  ) : null}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="z-[--swivu-bryntum-z-index] w-72 overflow-hidden rounded-lg border-border/60 p-0 shadow-xl"
              onWheel={(e) => e.stopPropagation()}
            >
              {popoverBody}
            </PopoverContent>
          </Popover>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearchQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            role="combobox"
            aria-expanded={open}
            className={cn(
              "flex h-swivu-input w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm",
              "ring-offset-background outline-none transition-colors duration-150 ease-out",
              "hover:bg-accent/40",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              open && "border-ring bg-accent/30",
              !selectedUsers.length && "text-muted-foreground",
            )}
          >
            {selectedUsers.length > 0 ? (
              <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                <div className="flex items-center -space-x-1.5">
                  {selectedUsers.slice(0, 3).map((user) => (
                    <UserAvatar
                      key={user.id}
                      user={user}
                      size="small"
                      showTooltip={true}
                      showRemove={false}
                      avatarClassName="!size-6 text-[10px] ring-1 ring-background"
                    />
                  ))}
                  {selectedUsers.length > 3 ? (
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] text-muted-foreground ring-1 ring-background">
                      +{selectedUsers.length - 3}
                    </span>
                  ) : null}
                </div>
                <span className="truncate text-foreground">
                  {selectedUsers.length === 1
                    ? selectedUsers[0].name
                    : `${selectedUsers.length} people`}
                </span>
              </div>
            ) : (
              <span>Select users…</span>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 opacity-50 transition-transform duration-150 ease-out",
                open && "rotate-180",
              )}
              aria-hidden
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[--swivu-bryntum-z-index] w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-lg border-border/60 p-0 shadow-xl"
          onWheel={(e) => e.stopPropagation()}
        >
          {popoverBody}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function ChecklistAssignedAvatars({
  checklist,
  max = 3,
  className,
}: {
  checklist: Checklist;
  max?: number;
  className?: string;
}) {
  const { project } = useSwivuBryntumContext();
  const assignedUsers = (checklist.assignedBoardUsers ?? [])
    .map((id) => project?.boardUsersStore?.getById?.(id) as BoardUserLike | undefined)
    .filter((user): user is BoardUserLike => Boolean(user?.id && user.name));

  if (!assignedUsers.length) return null;

  return (
    <div className={cn("flex items-center -space-x-1.5", className)} aria-label={`${assignedUsers.length} assigned`}>
      {assignedUsers.slice(0, max).map((user) => (
        <UserAvatar key={user.id} user={user} size="small" showTooltip={true} showRemove={false} />
      ))}
      {assignedUsers.length > max ? (
        <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-muted px-1.5 text-xs text-muted-foreground ring-2 ring-background">
          +{assignedUsers.length - max}
        </span>
      ) : null}
    </div>
  );
}
